export type SocialNetworkName = 'instagram' | 'facebook' | 'tiktok' | 'x' | 'linkedin';

export interface ValidationResult {
  isValid: boolean;
  errorMessage: string | null;
}

/**
 * Validates TikTok handle
 * Rules:
 * - Letters (a-z), Numbers (0-9), Underscore (_), Period (.)
 * - No spaces
 * - No other special characters
 * - Periods can't be consecutive or at the beginning/end
 */
export function validateTikTokHandle(handle: string): ValidationResult {
  if (!handle || handle.trim() === '') {
    return { isValid: true, errorMessage: null };
  }

  // Check for spaces
  if (/\s/.test(handle)) {
    return { isValid: false, errorMessage: 'TikTok handle cannot contain spaces' };
  }

  // Check for invalid characters (only allow letters, numbers, underscore, period)
  if (!/^[a-z0-9_.]+$/i.test(handle)) {
    return { isValid: false, errorMessage: 'TikTok handle can only contain letters, numbers, underscore (_), and period (.)' };
  }

  // Check if starts or ends with period
  if (handle.startsWith('.') || handle.endsWith('.')) {
    return { isValid: false, errorMessage: 'TikTok handle cannot start or end with a period' };
  }

  // Check for consecutive periods
  if (handle.includes('..')) {
    return { isValid: false, errorMessage: 'TikTok handle cannot contain consecutive periods' };
  }

  return { isValid: true, errorMessage: null };
}

/**
 * Validates Instagram handle
 * Rules:
 * - Letters (a-z), Numbers (0-9), Underscore (_), Period (.)
 * - No spaces
 * - No other special characters
 * - Cannot start or end with a period
 */
export function validateInstagramHandle(handle: string): ValidationResult {
  if (!handle || handle.trim() === '') {
    return { isValid: true, errorMessage: null };
  }

  // Check for spaces
  if (/\s/.test(handle)) {
    return { isValid: false, errorMessage: 'Instagram handle cannot contain spaces' };
  }

  // Check for invalid characters (only allow letters, numbers, underscore, period)
  if (!/^[a-z0-9_.]+$/i.test(handle)) {
    return { isValid: false, errorMessage: 'Instagram handle can only contain letters, numbers, underscore (_), and period (.)' };
  }

  // Check if starts or ends with period
  if (handle.startsWith('.') || handle.endsWith('.')) {
    return { isValid: false, errorMessage: 'Instagram handle cannot start or end with a period' };
  }

  return { isValid: true, errorMessage: null };
}

/**
 * Validates Facebook handle
 * Rules:
 * - Letters (a-z), Numbers (0-9), Period (.)
 * - No underscores
 * - No hyphens
 * - No spaces or other symbols
 * - Periods can't be consecutive or at the beginning/end
 */
export function validateFacebookHandle(handle: string): ValidationResult {
  if (!handle || handle.trim() === '') {
    return { isValid: true, errorMessage: null };
  }

  // Check for spaces
  if (/\s/.test(handle)) {
    return { isValid: false, errorMessage: 'Facebook handle cannot contain spaces' };
  }

  // Check for underscores
  if (handle.includes('_')) {
    return { isValid: false, errorMessage: 'Facebook handle cannot contain underscores' };
  }

  // Check for hyphens
  if (handle.includes('-')) {
    return { isValid: false, errorMessage: 'Facebook handle cannot contain hyphens' };
  }

  // Check for invalid characters (only allow letters, numbers, period)
  if (!/^[a-z0-9.]+$/i.test(handle)) {
    return { isValid: false, errorMessage: 'Facebook handle can only contain letters, numbers, and period (.)' };
  }

  // Check if starts or ends with period
  if (handle.startsWith('.') || handle.endsWith('.')) {
    return { isValid: false, errorMessage: 'Facebook handle cannot start or end with a period' };
  }

  // Check for consecutive periods
  if (handle.includes('..')) {
    return { isValid: false, errorMessage: 'Facebook handle cannot contain consecutive periods' };
  }

  return { isValid: true, errorMessage: null };
}

/**
 * Validates LinkedIn handle
 * Rules:
 * - Letters (a-z), Numbers (0-9), Hyphen (-)
 * - No underscores
 * - No periods
 * - No spaces or special characters
 */
export function validateLinkedInHandle(handle: string): ValidationResult {
  if (!handle || handle.trim() === '') {
    return { isValid: true, errorMessage: null };
  }

  // Check for spaces
  if (/\s/.test(handle)) {
    return { isValid: false, errorMessage: 'LinkedIn handle cannot contain spaces' };
  }

  // Check for underscores
  if (handle.includes('_')) {
    return { isValid: false, errorMessage: 'LinkedIn handle cannot contain underscores' };
  }

  // Check for periods
  if (handle.includes('.')) {
    return { isValid: false, errorMessage: 'LinkedIn handle cannot contain periods' };
  }

  // Check for invalid characters (only allow letters, numbers, hyphen)
  if (!/^[a-z0-9-]+$/i.test(handle)) {
    return { isValid: false, errorMessage: 'LinkedIn handle can only contain letters, numbers, and hyphen (-)' };
  }

  return { isValid: true, errorMessage: null };
}

/**
 * Validates X (formerly Twitter) handle
 * Rules:
 * - Letters (a-z), Numbers (0-9), Underscore (_)
 * - No periods
 * - No hyphens
 * - No spaces or special characters
 */
export function validateXHandle(handle: string): ValidationResult {
  if (!handle || handle.trim() === '') {
    return { isValid: true, errorMessage: null };
  }

  // Check for spaces
  if (/\s/.test(handle)) {
    return { isValid: false, errorMessage: 'X handle cannot contain spaces' };
  }

  // Check for periods
  if (handle.includes('.')) {
    return { isValid: false, errorMessage: 'X handle cannot contain periods' };
  }

  // Check for hyphens
  if (handle.includes('-')) {
    return { isValid: false, errorMessage: 'X handle cannot contain hyphens' };
  }

  // Check for invalid characters (only allow letters, numbers, underscore)
  if (!/^[a-z0-9_]+$/i.test(handle)) {
    return { isValid: false, errorMessage: 'X handle can only contain letters, numbers, and underscore (_)' };
  }

  return { isValid: true, errorMessage: null };
}

/**
 * Validates a social handle based on the platform
 */
export function validateSocialHandle(platform: SocialNetworkName, handle: string): ValidationResult {
  switch (platform) {
  case 'tiktok':
    return validateTikTokHandle(handle);
  case 'instagram':
    return validateInstagramHandle(handle);
  case 'facebook':
    return validateFacebookHandle(handle);
  case 'linkedin':
    return validateLinkedInHandle(handle);
  case 'x':
    return validateXHandle(handle);
  default:
    return { isValid: true, errorMessage: null };
  }
}

